import { useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useIsEditForm } from "@/shared/contexts/IsEditFormContext";
import {
    trackHelpUsageTextClick,
    trackHelpUsageTextModalClosed,
    trackHelpUsageTextModalViewed,
} from "@/shared/lib/ube";
import { Callout, Modal } from "@/shared/ui/ancestry";
import { useFieldContext } from "./FieldContext";
import styles from "./FieldUsage.module.scss";

// Small reusable HTML renderer
const HtmlContent = ({ html, className }: { html: string; className?: string }) => (
    <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

// Modal strategy - self-contained modal
const ModalUsage = ({
    exampleText,
    descriptionHtml,
    viewTitle,
    fieldName,
}: {
    exampleText: string;
    descriptionHtml: string;
    viewTitle: string | undefined;
    fieldName: string | undefined;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();

    const handleClick = () => {
        if (fieldName) trackHelpUsageTextClick(fieldName);
        setIsOpen(true);
        if (fieldName) trackHelpUsageTextModalViewed(fieldName);
    };

    const handleClose = () => {
        setIsOpen(false);
        if (fieldName) trackHelpUsageTextModalClosed(fieldName);
    };

    return (
        <>
            <button type="button" className={classNames("link", styles.usageButton)} onClick={handleClick}>
                {exampleText}
            </button>
            <Modal open={isOpen} onRequestClose={handleClose} title={viewTitle}>
                <HtmlContent html={descriptionHtml} className={styles.fieldUsageContent} />
                <button
                    type="button"
                    className={classNames("ancBtn silver", styles.closeButton)}
                    onClick={handleClose}
                    data-testid="field-usage-close-button"
                >
                    {t("common.close")}
                </button>
            </Modal>
        </>
    );
};

// Callout strategy
const CalloutUsage = ({
    exampleText,
    descriptionHtml,
    fieldName,
}: {
    exampleText: string;
    descriptionHtml: string;
    fieldName: string | undefined;
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);

    const handleClick = () => {
        if (fieldName) trackHelpUsageTextClick(fieldName);
    };

    return (
        <>
            <button
                type="button"
                className={classNames("link iconAfter iconArrowSmallDownAfter", styles.usageButton)}
                ref={triggerRef}
                onClick={handleClick}
            >
                {exampleText}
            </button>

            <Callout trigger={triggerRef} open={false}>
                <HtmlContent
                    html={descriptionHtml}
                    className={classNames("fieldUsageText calloutDomContent", styles.fieldUsageContent)}
                />
            </Callout>
        </>
    );
};

// Main component
export const FieldUsage = () => {
    const { fieldConfig } = useFieldContext();
    const isEditForm = useIsEditForm();

    if (!fieldConfig.guidance) return null;

    const { exampleText, descriptionHtml, viewType, viewTitle, fieldName } = fieldConfig.guidance;

    // In the edit form modal, suppress the Modal-type usage to avoid nested modal conflicts.
    if (viewType === "Modal" && isEditForm) {
        return null;
    }

    return viewType === "Modal" ? (
        <ModalUsage
            exampleText={exampleText}
            descriptionHtml={descriptionHtml}
            viewTitle={viewTitle}
            fieldName={fieldName}
        />
    ) : (
        <CalloutUsage exampleText={exampleText} descriptionHtml={descriptionHtml} fieldName={fieldName} />
    );
};
